import { toast as sonner } from 'vue-sonner'

export type ToastVariant = 'default' | 'success' | 'error' | 'warning'

export interface Toast {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  duration: number
}

export type ToastInput = Omit<Toast, 'id'>

/**
 * Composable for managing toast notifications
 * Works alongside shadcn-vue's Sonner component
 */
export function useToast() {
  /**
   * Show a toast notification
   */
  function toast(options: ToastInput): string | number {
    const sonnerOptions = {
      description: options.description,
      duration: options.duration || 5000,
    }

    switch (options.variant) {
      case 'success':
        return sonner.success(options.title, sonnerOptions)
      case 'error':
        return sonner.error(options.title, sonnerOptions)
      case 'warning':
        return sonner.warning(options.title, sonnerOptions)
      default:
        return sonner(options.title, sonnerOptions)
    }
  }

  /**
   * Dismiss a toast by ID
   */
  function dismiss(id: string | number): void {
    sonner.dismiss(id)
  }

  /**
   * Dismiss all toasts
   */
  function dismissAll(): void {
    sonner.dismiss()
  }

  /**
   * Show a success toast
   */
  function success(title: string, description?: string): string | number {
    return toast({ title, description, variant: 'success', duration: 5000 })
  }

  /**
   * Show an error toast
   */
  function error(title: string, description?: string): string | number {
    return toast({ title, description, variant: 'error', duration: 7000 })
  }

  /**
   * Show a warning toast
   */
  function warning(title: string, description?: string): string | number {
    return toast({ title, description, variant: 'warning', duration: 5000 })
  }

  /**
   * Show an info toast (default variant)
   */
  function info(title: string, description?: string): string | number {
    return toast({ title, description, variant: 'default', duration: 5000 })
  }

  return {
    toast,
    dismiss,
    dismissAll,
    success,
    error,
    warning,
    info,
  }
}
